/**
 * Smart schema routing based on page context
 * Determines which schemas to inject based on the current page
 */

import {
  generateOrganizationSchema,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateAboutPageSchema,
  generatePersonSchema,
  generateServicesPageSchema,
  generateServicesFAQSchema,
} from "./schema";

export type PageType = 
  | "home"
  | "about"
  | "services"
  | "portfolio"
  | "blog-post"
  | "blog-listing"
  | "pricing"
  | "discovery"
  | "pasadena-web-design"
  | "admin"
  | "api"
  | "not-found"
  | "other";

export interface SchemaContext {
  pathname: string;
  pageType?: PageType;
  pageData?: any;
}

/**
 * Determines the page type based on pathname
 */
export function detectPageType(pathname: string): PageType {
  // Normalize pathname
  const normalizedPath = pathname.toLowerCase().replace(/\/$/, '');
  
  if (normalizedPath === '' || normalizedPath === '/') {
    return 'home';
  }
  
  if (normalizedPath.startsWith('/admin')) {
    return 'admin';
  }
  
  if (normalizedPath.startsWith('/api')) {
    return 'api';
  }
  
  if (normalizedPath === '/about') {
    return 'about';
  }
  
  if (normalizedPath === '/services') {
    return 'services';
  }
  
  if (normalizedPath === '/portfolio') {
    return 'portfolio';
  }
  
  if (normalizedPath === '/pricing') {
    return 'pricing';
  }
  
  if (normalizedPath === '/discovery') {
    return 'discovery';
  }
  
  if (normalizedPath === '/blog') {
    return 'blog-listing';
  }
  
  if (normalizedPath.match(/^\/blog\/[^\/]+\/[^\/]+$/)) {
    return 'blog-post';
  }
  
  if (normalizedPath === '/pasadena-web-design') {
    return 'pasadena-web-design';
  }
  
  if (normalizedPath === '/not-found' || normalizedPath === '/404') {
    return 'not-found';
  }
  
  return 'other';
}

/**
 * Returns appropriate schemas based on page context
 */
export function getPageSchemas(context: SchemaContext): any[] {
  const pageType = context.pageType || detectPageType(context.pathname);
  const schemas: any[] = [];
  
  switch (pageType) {
    case 'home':
      // Home page gets business and service preview schemas
      schemas.push(
        generateOrganizationSchema(),
        generateLocalBusinessSchema(),
        ...generateServiceSchema()
      );
      break;
      
    case 'about':
      // About page gets organization, person, and about page schemas
      schemas.push(
        generateOrganizationSchema(),
        generatePersonSchema(),
        generateAboutPageSchema()
      );
      break;
      
    case 'services':
      // Services page gets organization, all service schemas, and services FAQ
      schemas.push(
        generateOrganizationSchema(),
        ...generateServiceSchema(),
        generateServicesPageSchema(),
        generateServicesFAQSchema()
      );
      break;
      
    case 'portfolio':
      // Portfolio page gets organization and service schemas
      schemas.push(
        generateOrganizationSchema(),
        ...generateServiceSchema()
      );
      break;
      
    case 'blog-post':
      // Blog posts only get article schema (handled in the page component)
      // No schemas injected at layout level
      break;
      
    case 'blog-listing':
      // Blog listing gets organization schema only
      schemas.push(generateOrganizationSchema());
      break;
      
    case 'pricing':
      // Pricing page gets organization and service schemas
      schemas.push(
        generateOrganizationSchema(),
        ...generateServiceSchema()
      );
      break;
      
    case 'discovery':
      // Discovery page gets organization and local business schemas
      schemas.push(
        generateOrganizationSchema(),
        generateLocalBusinessSchema()
      );
      break;
      
    case 'pasadena-web-design':
      // Pasadena page gets organization and local business schemas
      // Note: The page itself adds a more specific LocalBusiness schema with Pasadena area served
      schemas.push(
        generateOrganizationSchema()
      );
      break;
      
    case 'admin':
    case 'api':
    case 'not-found':
      // No schemas for admin, API, or error pages
      break;
      
    default:
      // Other pages get minimal organization schema
      schemas.push(generateOrganizationSchema());
  }
  
  return schemas;
}